import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { useDocumentFile } from 'hooks/imageHooks/useDocFile';
import { FileUpload } from 'components/common/FileUpload';
import { Document } from 'models/DocumentModel';
import AsyncSelect from 'react-select/async';
import { useSearchParams } from 'react-router-dom';


const DocumentForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.DOCUMENT, Code.Form.Name.DOCUMENT];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [typeOptions, setTypeOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [doctorOptions, setDoctorOptions] = Hooks.Form.Options();
  const [issueOptions, setIssueOptions] = Hooks.Form.Options();
  const [formatOptions, setFormatOptions] = Hooks.Form.Options();
  const [folderOptions, setFolderOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Document>({});

  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [isFileAttached, setIsFileAttached] = useState(false);
  const { handleFileChange, handleFileAttach, handleFileDelete } = useDocumentFile(fileName, setFileName, setIsFileAttached, setError);

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
    const doctorId = searchParams.get(Code.UrlPara.Doctor);
    const examId = searchParams.get(Code.UrlPara.Exam);
  
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_TYPE, Code.Option.Common.SELECT, setTypeOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    Files.Json.API(Code.Form.Name.DOCTOR, Code.Option.Common.ALL, setDoctorOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_ISSUE, Code.Option.Common.NA, setIssueOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_FORMAT, Code.Option.Common.NA, setFormatOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.DOCUMENT_FOLDER, Code.Option.Common.NA, setFolderOptions, setError);
   
    API.Data.GetByKeyId<Document>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.document_active!); 
        
        const isValid = await Utility.isValid.File(data.document_file_name);
        setFileName(data.document_file_name);
        setIsFileAttached(isValid);

        const clientData = await API.Data.getClientNameNumber(data.client_id);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          ...clientData,
        }));
        setLoading(false);
      }})

      const onNewEntry = async () => {
        if(keyId===Code.Key.NEW && clientId) {
          const clientName = await API.Data.GetClientName(Number(clientId));
          setFormData((prevData) => ({
            ...prevData,
            doctor_id: doctorId ? Number(doctorId) : 2,
            exam_id: examId ? Number(examId) : 2,
            client_id: Number(clientId),
            client_name: Utility.Value.Text(clientName), 
          }));
        }
        setLoading(false);
      };
      onNewEntry();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
    });

    finalData.form_id = Code.Form.Id.CLIENT;
    finalData.document_file_name = fileName;

    //console.log("-----newid---->",keyId);

    API.Data.Save(formName, keyId, finalData, setMessage)
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} clientNumber={formData.client_number} formName={formName} Privilege={Privilege} print={true} />
          <Control.Tab.Header titles={["Document","More"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
          
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Select id={Name.Common.TypeId} value={formData.type_id} title={Title.Type} options={typeOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} require={true} />
                <Field.Select id={Name.Common.IssueId} value={formData.issue_id} title="Issue by" options={issueOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Doctor.Id} value={formData.doctor_id} title={Title.Doctor} options={doctorOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Text id={Name.Document.FileNo} value={formData.document_file_no} title={Title.FileNo} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Box>
                <Field.Text id={Name.Document.Name} value={formData.document_name} title={Title.Title} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Box>
              <Control.Box>
                <FileUpload isFileAttached={isFileAttached} handleFileChange={handleFileChange} handleFileAttach={handleFileAttach} handleFileDelete={handleFileDelete} />
              </Control.Box>
            </Control.Tab.Content>
            
            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Text id={Name.Document.RefNo} value={formData.document_refno} title={Title.Refno} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Select id={Name.Common.FormatId} value={formData.format_id} title="Availability" options={formatOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
                <Field.Select id={Name.Common.FolderId} value={formData.folder_id} title="Folder/Rack No." options={folderOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Date id={Name.Document.IssueDate} value={formData.document_issue_date} title={Title.Issue} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Date id={Name.Document.ExpiryDate} value={formData.document_expiry_date} title={Title.Expiry} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Document.Comment} value={formData.document_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
            </Control.Tab.Content>
          
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} print={true} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default DocumentForm;